import { defineStore } from "pinia";
import {
    useAppState,
    useAppState as useAppStateStore,
} from "../../../Stores/AppState";
import { useWorkspace } from "../../../Stores/Workspaces";
import { useConversation } from "../../../Stores/Conversation";

export const useChat = defineStore("Chat", {
    state: () => ({
        message: null,
        tab: "message",
        attached_files: null,
        annotated_file: null,
        is_message_tiptap_clear: false,
        message_reply_to: null,
        is_to_focus: false,
        active_update: null,
        active_update_message: null,
        is_user_typing: false,
        is_annotating: null,
        is_annotated: null,
        annotating_conversation_sid: null,
        active_conv_sid: null,
        newly_added_message_total_height: 0,
        is_scroll_disabled: false,
        chat_stream_loader: true,
        activity_textbox_height: 112,
        is_activity_dragged: false,
        is_message_editor_focus: false,
        is_checklist_active: false,
        topbar_avatar_active_menu: null,
        urgent_request: false,
        request_support_user: null,
        previous_tab: "message",
        member_panel_tab_before_open_membership: null,
        conv_id_before_open_membership: null,
        membership_prefetched_conversation_id: null,
        is_empty: true,
        is_loading: false,
        message_activity: [],
        dashboard_default_data: null,
    }),
    getters: {
        is_comment_tab: (state) => state.tab === "comment",
        is_member_tab: (state) => state.tab === "member",
        is_message_tab: (state) => state.tab === "message",
        is_dashboard_tab: (state) => state.tab === "dashboard",
    },
    actions: {
        setMessage(val) {
            this.message = val;
        },

        resetMessage() {
            this.setMessage(null);
        },

        setTab(val) {
            this.tab = val;
        },

        setCommentTab() {
            this.setTab("comment");
        },

        setMessageTab() {
            this.setTab("message");
        },

        setMemberTab() {
            if (this.tab === "member") {
                this.member_panel_tab_before_open_membership =
                    useAppState().selected_variant_tab;
            }
            this.setTab("member");
        },

        toggleMemberTab() {
            if (this.tab === "member") {
                this.member_panel_tab_before_open_membership =
                    useAppState().selected_variant_tab;
            }

            this.setTab(this.tab === "member" ? "message" : "member");
        },

        setDashboardTab() {
            this.setTab("dashboard");
        },

        setDashboardDefaultData(data) {
            this.dashboard_default_data = data;
        },

        attachFiles(val) {
            this.attached_files = val;
        },

        annotatedFile(val) {
            this.annotated_file = val;
        },

        resetAttachFiles() {
            this.attachFiles(null);
            this.annotatedFile(null);
        },

        adjustTextBox(value = this.getTextAreaHeight()) {
            let target = document.querySelector(".prospus-text-area-wrap");
            target.style.height = value + "px";
            this.activity_textbox_height = target.clientHeight + 53;
        },

        getTextAreaHeight(
            selector = "#editor-workspace-textarea .ProseMirror",
        ) {
            const element = document.querySelector(selector);
            const prev_scroll_height = element.scrollHeight;

            element.classList.add("removed-height");

            const current_scroll_height = element.scrollHeight;
            let final_scrollHeight = prev_scroll_height;

            if (prev_scroll_height - 9 < current_scroll_height)
                final_scrollHeight =
                    current_scroll_height === 42
                        ? current_scroll_height
                        : current_scroll_height + 19;

            setTimeout(() => {
                element.classList.remove("removed-height");
            }, 1);
            return final_scrollHeight + 18;
        },

        setMessageTiptapClear(val) {
            this.is_message_tiptap_clear = val;
        },

        openConversation(force = true) {
            const app_state_store = useAppStateStore();
            const workspace_store = useWorkspace();

            const conversation_store = useConversation();
            const conversation = conversation_store.active;

            if (conversation?.isToShowHint() && !force) {
                app_state_store.toggleInviteDrawer(
                    true,
                    workspace_store.active_chat_data,
                );
                this.setMemberTab();
            } else {
                this.setMessageTab();
                this.setFocusOnEditor();
            }
        },

        setMessageReplyTo(message) {
            this.message_reply_to = message;
        },

        setFocusOnEditor(val = true) {
            this.is_to_focus = val;
        },

        setActiveMessageUpdate(val) {
            this.active_update = val;
        },

        setActiveMessageUpdateMessage(val) {
            this.active_update_message = val;
        },

        setUserTyping(val) {
            this.is_user_typing = val;
        },

        setAnnotated(val) {
            this.is_annotated = val;
        },

        setAnnotating(val, conversationSid = null) {
            this.is_annotating = val;
            if (val !== null && conversationSid !== null) {
                this.annotating_conversation_sid = conversationSid;
            }
        },
        
        clearAnnotatingConversationSid() {
            this.annotating_conversation_sid = null;
        },

        resetTextAreaHeight() {
            const input = document.getElementById("new-message-input");

            if (input) {
                input.style.setProperty("height", null);
                input.style.setProperty("overfolow", null);
            }
        },

        setActiveConvSid(val) {
            this.active_conv_sid = val;
        },

        resetTopbarActivity() {
            this.is_annotated = null;
            this.message_reply_to = null;
            this.active_update = null;
        },

        disableScroll() {
            this.is_scroll_disabled = true;
        },

        enableScroll() {
            this.is_scroll_disabled = false;
        },

        scrollToBottom(is_smooth = false) {
            if (this.is_scroll_disabled) return;

            const container = document.querySelector(
                ".prospus-messages-container__wrapper",
            );

            if (container)
                container.scrollTo({
                    top: container.scrollHeight,
                    behavior: is_smooth ? "smooth" : "instant",
                });
        },

        skipChatStreamLoader() {
            this.chat_stream_loader = false;
        },

        continueChatStreamLoader() {
            this.chat_stream_loader = true;
        },

        setMessageEditorFocus() {
            this.is_message_editor_focus = true;
        },

        setMessageEditorOffFocus() {
            this.is_message_editor_focus = false;
        },

        setActiveChecklist() {
            this.is_checklist_active = true;
        },

        setInactiveChecklist() {
            this.is_checklist_active = false;
        },

        toggleChecklist() {
            this.is_checklist_active = !this.is_checklist_active;
        },

        onActivityAdjustStart() {
            this.is_activity_dragged = true;
        },

        onActivityAdjustEnd() {
            this.is_activity_dragged = false;
        },

        setTopbarAvatarActiveMenu(val) {
            this.topbar_avatar_active_menu = val;
        },

        setPreviousTab(tab) {
            this.previous_tab = tab;
        },

        getBackToPreviousTab() {
            this.tab = this.previous_tab;
        },

        setPreviousConvID(value) {
            this.conv_id_before_open_membership = value;
        },

        setMembershipPrefetchedConversationId(value) {
            this.membership_prefetched_conversation_id = value;
        },

        toggleLoading(val) {
            this.is_loading = val ?? !this.is_loading;
        },

        setMessageActivity(conversation_id, data, type, value = null) {
            this.removeMessageActivity(conversation_id);
            
            const value_body = value || data?.body || null;
            this.message_activity.push({
                id: conversation_id,
                type,
                data: data,
                value: value_body
            })
        },

        removeMessageActivity(conversation_id) {
            const index = this.message_activity.findIndex(item => item.id === conversation_id);
            if (index !== -1) {
                this.message_activity.splice(index, 1);
            }
        },

        getMessageActivity(conversation_id) {
            return this.message_activity.find(item => item.id === conversation_id);
        },

        getMessageActivityType(conversation_id, type) {
            return this.message_activity.find(item => item.id === conversation_id && item.type === type);
        },

        getMessageActivityTypeIndex(conversation_id, type) {
            return this.message_activity.findIndex(item => item.id === conversation_id && item.type === type);
        },

        updateMessageActivityEditBody(conversation_id, body) {
            const index = this.getMessageActivityTypeIndex(conversation_id, 'edit');
            if (index !== -1) {
                this.message_activity[index].data.body = body;
            }
        },

        updateMessageActivityValue(conversation_id, value, type) {
            const index = this.getMessageActivityTypeIndex(conversation_id, type);

            if (index !== -1) {
                this.message_activity[index].value = value;
            }
        }
    },
});
